import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, RefreshCw } from 'lucide-react';
import { chatWithAI } from '../lib/openrouter'; // Assuming openrouter.ts handles its own internal retries for API keys/models
import ReactMarkdown from 'react-markdown';

interface ChatMessage {
  id: string; // Added for unique key prop
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean; // To style error messages differently
}

const MAX_CHAT_RETRIES = 35; // Estava 5, Max retries for a single user message if chatWithAI fails
const RETRY_DELAY_BASE_CHAT = 2500; // Initial delay for chat retries

const ChatInterface: React.FC = () => {
  const initialMessages: ChatMessage[] = [
    {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: `**Oi, eu sou a Aurora** ✨  
Sua assistente de IA especialista em Consultoria de Negócios, Vendas, Marketing Digital e Anúncios.  

Estou aqui para te ajudar com:  
- 🎯 Estratégias de segmentação  
- 📊 Otimização de campanhas  
- 🔍 Análise de público-alvo  
- 💡 Ideias criativas para anúncios  
- 📈 Dúvidas gerais sobre marketing, negócios ou crescimento  

Como posso te ajudar hoje?`
    }
  ];

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentRetryCountRef = useRef(0);
  const currentMessageAttemptRef = useRef<ChatMessage | null>(null); // Stores the user message being retried
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const handleSendMessage = useCallback(async (messageContent: string, isRetry: boolean = false) => {
    if (!isRetry) {
      const userMessage: ChatMessage = { id: `user-${Date.now()}`, role: 'user', content: messageContent };
      setMessages(prev => [...prev, userMessage]);
      currentMessageAttemptRef.current = userMessage; // Store for potential retries
      currentRetryCountRef.current = 0;
      setInput(''); // Clear input after user sends message
    }
    
    setIsLoading(true);

    try {
      // chatWithAI from openrouter.ts should ideally handle its own model/key cycling and retries
      // This component's retry is for when chatWithAI itself throws a final error after its attempts
      const responseContent = await chatWithAI([{ role: 'user', content: messageContent }]);
      
      if (responseContent && responseContent.trim() !== '') {
        setMessages(prev => [...prev, { id: `assistant-${Date.now()}`, role: 'assistant', content: responseContent }]);
      } else {
        // Handle empty but successful response as a form of soft error, trigger retry if needed
        throw new Error('Resposta da IA vazia ou inválida.');
      }
      
      setIsLoading(false);
      currentRetryCountRef.current = 0;
      currentMessageAttemptRef.current = null;

    } catch (error) {
      console.error('Erro ao enviar mensagem para IA:', error);
      currentRetryCountRef.current++;

      if (currentRetryCountRef.current < MAX_CHAT_RETRIES) {
        const delay = RETRY_DELAY_BASE_CHAT * Math.pow(1.5, currentRetryCountRef.current -1 );
        console.log(`ChatInterface: Tentativa ${currentRetryCountRef.current} falhou. Tentando novamente em ${delay}ms.`);
        retryTimeoutRef.current = setTimeout(() => {
          if (currentMessageAttemptRef.current) { // Ensure we have the message to retry
            handleSendMessage(currentMessageAttemptRef.current.content, true); // Pass original content for retry
          }
        }, delay);
        // No setIsLoading(false) here, as we are still in a loading/retrying state
      } else {
        console.error('ChatInterface: Máximo de retentativas atingido.');
        setMessages(prev => [
          ...prev,
          {
            id: `assistant-error-${Date.now()}`,
            role: 'assistant',
            content: 'Desculpe, não consegui processar sua mensagem após várias tentativas. Por favor, tente reenviar ou reformular sua pergunta.',
            isError: true
          }
        ]);
        setIsLoading(false);
        currentRetryCountRef.current = 0;
        // Do not clear currentMessageAttemptRef.current here, allow user to retry manually if they wish
        // Or, restore input for user to edit:
        if (currentMessageAttemptRef.current) {
            setInput(currentMessageAttemptRef.current.content); 
        }
      }
    }
  }, []); // Removed messages from dependencies to avoid re-triggering on message list change

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    handleSendMessage(input.trim());
  };

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id} // Use unique ID for key
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : message.isError 
                    ? 'bg-red-100 dark:bg-red-800/50 border border-red-200 dark:border-red-700'
                    : message.id.startsWith('assistant-initial') // Check if it's the initial assistant message
                      ? 'bg-indigo-50 dark:bg-indigo-900/50 border border-indigo-100 dark:border-indigo-800'
                      : 'bg-gray-100 dark:bg-gray-700'
              }`}
            >
              {message.role === 'assistant' && message.id.startsWith('assistant-initial') && (
                <Bot className="h-5 w-5 mb-2 text-indigo-600 dark:text-indigo-300" />
              )}
              <div className={`prose prose-sm ${
                message.role === 'user'
                  ? 'text-white'
                  : message.isError 
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-gray-800 dark:text-gray-200'
              }`}>
                <ReactMarkdown>{message.content}</ReactMarkdown>
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t dark:border-gray-700">
        <div className="flex space-x-4">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Digite sua mensagem..."
            className="flex-1 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;